import React, { useEffect } from 'react';
import { FocusTrap, mq } from '@kvdbil/components';
import styled, { createGlobalStyle, css } from 'styled-components';
import { useDispatch } from 'react-redux';
import { resetMenuDrawer } from '../shared/actions/drawerMenu';
import { useTranslation } from '~/Locale';

type Direction = 'left' | 'right' | 'top' | 'bottom';

const getTransform = (direction: Direction) => {
  switch (direction) {
    case 'left':
      return 'translateX(-100%)';
    case 'right':
      return 'translateX(100%)';
    case 'top':
      return 'translateY(-100%)';
    case 'bottom':
      return 'translateY(100%)';
    default:
      return 'translateX(-100%)';
  }
};

const DrawerContainer = styled.div<{ isOpen: boolean; direction: Direction }>`
  top: 0;
  position: fixed;
  ${({ direction }) => css`
    ${direction}: 0;
    transform: ${getTransform(direction)};
  `}

  background-color: ${({ theme }) => theme.colors.background.light};
  transition: transform 0.2s ease-in-out;

  z-index: 999;
  width: 100%;
  height: 100%;

  ${mq('tablet')} {
    max-width: 425px;
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: none;
    `}
`;

const Backdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(29, 29, 27, 0.4);
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
  z-index: 998;
`;

const ScrollLock = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;

type Props = {
  isOpen: boolean;
  useBackdrop?: boolean;
  useScrollLock?: boolean;
  onBackdropClick?(): void;
  direction?: Direction;
  unmountOnClose?: boolean;
  children: React.ReactNode;
};

export const Drawer = ({
  isOpen,
  useBackdrop = true,
  useScrollLock = true,
  onBackdropClick,
  direction = 'right',
  unmountOnClose = true,
  children
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      !isOpen && dispatch(resetMenuDrawer());
    }, 200); // timeout number reflects CSS transition time (drawer fade)

    return () => clearTimeout(timeoutId);
  }, [isOpen, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        dispatch(resetMenuDrawer());
      }
    };

    isOpen && window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, dispatch]);

  if (!isOpen && unmountOnClose) {
    return null;
  }

  return (
    <>
      {isOpen && useScrollLock && <ScrollLock />}

      {useBackdrop && (
        <Backdrop
          data-testid="drawer-component-backdrop"
          isOpen={isOpen}
          onClick={onBackdropClick}
        />
      )}

      <DrawerContainer
        isOpen={isOpen}
        direction={direction}
        role="dialog"
        aria-modal="true"
        aria-label={t('Side panel')}
        aria-hidden={!isOpen}
      >
        <FocusTrap pause={!isOpen}>{children}</FocusTrap>
      </DrawerContainer>
    </>
  );
};
